<template>
  <div id="preview-wrapper">

    <div id="preview-video" class="video-background">
      <loader v-if="showLoader" :active.sync="showLoader" :can-cancel="false" :is-full-page="false"/>
      <div class="track-holder" v-else>
        <twilio-track
          v-for="track in localTracks"
          v-if="track"
          :track="track"
          :renderTrack="true"
          :stopOnDestroy="stopOnDestroy"
          :key="track.id"
          :ref="`${track.kind}Track`"
        ></twilio-track>
      </div>
    </div>

  </div>
</template>

<script>
  import Loader from 'vue-loading-overlay'
  import MediaSelector from './MediaSelector.vue'
  import TwilioMixin from '@/../../shared/src/mixins/TwilioMixin.vue'
  import TwilioTrack from '@/../../shared/src/components/TwilioTrack'
  import { normaliseFirefoxTrackId } from '@/../../shared/src/utils'
  import { getPeerConnectionStatsById } from '@/../../shared/src/utils/twilioHelpers'
 /**
   * Component to render a full preview of the users video stream to the page
   * Also renders 2 MediaSelector Components as children to choose the user's input devices
  */
  export default {
    name: 'TwilioVideoPreview',

    mixins: [ TwilioMixin ],

    props: {
      mediaOpts: {
        'type': Object,
        'default': null
      },
      stopOnDestroy: {
        'type': Boolean,
        'default': true
      }
    },

    data () {
      return {
        'logLevel': 'warn',
        'localTracks': [],
        'statsCount': 0,
        'statsCountLimit': 30,
        'statLogUrl': '',
        'statsCollection': [],
        'room': null,
        'resetFails': 0,
        'showLoader': false
      }
    },

    components: {
      'media-selector': MediaSelector,
      'twilio-track': TwilioTrack,
      'loader': Loader
    },

    methods: {

      startPreviewTest () {
        return this.initAndConnect()
      },

      resetPreviewTest () {
        this.stopStats()
        this.statsCount = 0

        if (this.localTracks.length) {
          this.stopAnyLocalTracks()
          let time = 1000 + (3000 * this.resetFails)
          return new Promise((resolve, reject) => {
            window.setTimeout(() => { resolve() }, time)
          })
        }
        return Promise.resolve()
      },

      getTrackId (track) {
        return '_' + normaliseFirefoxTrackId(track.id)
      },

      noDevicesFound (inputType) {
        this.$emit('noDevicesFound', inputType)
      },

      docCamPreview () {
        this.showLoader = true
        return this.resetPreviewTest()
          .then(() => {
            return this.getNativeLocalTracks()
          })
          .then((mediaStream) => {
            this.localMediaStream = mediaStream
            return this.getTwilioTracksFromMediastreamTracks(mediaStream.getTracks())
          })
          .then((tracks) => {
            this.localTracks = tracks
            this.showLoader = false
          })
          .catch((err) => {
            console.log('Error getting local tracks', err)
          })
      },

      onboardingPreview () {
        this.showLoader = true
        return this.resetPreviewTest()
          .then(() => {
            return this.getLocalTracks()
          })
          .then((tracks) => {
            this.showLoader = false
            this.localTracks = tracks
            this.resetFails = 0
            return this.$emit('twilioPass')
          })
          .catch((err) => {
            if (this.resetFails < 3) {
              this.resetFails += 1
              return this.onboardingPreview()
            }
            this.showLoader = false
            return this.$emit('twilioFail', err)
          })
      },

      stopAnyLocalTracks () {

        const videoEl = this.$el.querySelector('video')
        const audioEl = this.$el.querySelector('audio')
        const audioTrack = this.localTracks.find(t => t.kind === 'audio')
        const videoTrack = this.localTracks.find(t => t.kind === 'video')

        if (videoEl && videoEl.srcObject) {
          videoEl.srcObject = null
        }
        if (audioEl && audioEl.srcObject) {
          audioEl.srcObject = null
        }
        if (audioTrack && audioTrack.stop) {
          audioTrack.stop()
        }
        if (videoTrack && videoTrack.stop) {
          videoTrack.stop()
        }

      },

      async dummyConnect () {
        const tokenResp = await this.requestRoom(this.tokenUrl.replace(this.config.user.id, '00'))
        const room = await this.connectToRoom(tokenResp.data.token, this.twilioEventId, {'tracks': []})
        this.gatherStats(room)
        this.room = room
        return room
      },

      initAndConnect () {
        return this.getLocalTracks()
          .then((tracks) => {
            this.localTracks = tracks
            return this.requestRoom()
          })
          .then((twilioResp) => {
            return this.connectToRoom(twilioResp.data.token, this.twilioEventId)
          })
          .then((room) => {
            return this.dummyConnect()
          })
          .catch((err) => {
            this.$emit('twilioFail', err)
          })
      },

      async actionStats ([statsReport]) {

        // Get peer connection stats and put roundTripTime into video
        const rawStats = await getPeerConnectionStatsById(this.room, statsReport.peerConnectionId)
        const roundTripTime = rawStats.currentRoundTripTime ? rawStats.currentRoundTripTime * 1000 : null
        const targetReport = statsReport.remoteVideoTrackStats[0] || statsReport.remoteAudioTrackStats[0]

        if (targetReport) {
          targetReport.roundTripTime = roundTripTime
        }

        this.statsCount += 1

        if (this.statsCount > this.statsCountLimit) {
          this.$emit('evaluateQualityTest')
          this.stopStats()
          this.logInfo({'type': 'troubleshooter/twilioStats', 'payload': {'stats': this.statsCollection}})
        }

        this.$emit('statsReport', {
          'video': statsReport.remoteVideoTrackStats[0],
          'audio': statsReport.remoteAudioTrackStats[0],
          'statsCount': this.statsCount,
          'statsCountLimit': this.statsCountLimit
        })

        this.logStats(statsReport)

      },

      logStats (statsReport) {
        this.statsCollection.push(statsReport)
      }

    },

    updated () {
      const audioEl = this.$el.querySelector('audio')
      if (audioEl) {
        this.$emit('audioTrackUpdated', audioEl.srcObject)
      }
    }

  }

</script>

<style scoped>

  .track-holder{
    /*width: 320px;*/
    position: relative;
    margin: auto;
  }

  #preview-video{
    /*min-height: 256px*/
  }

  #preview-video:after {
    padding-bottom: 74%;
    display: block;
    content: '';
  }

  .video-background{
    background: url(https://static.learncube.net/images/audio_only.png);
    background-repeat: no-repeat;
    background-size: 90%;
    background-color: #5D5D5D;
    background-position: center;
    border-radius: 4px;
    /*background: url(https://static.learncube.net/images/audio_only.png);*/
    /*background-repeat: no-repeat;*/
    /*background-origin: content-box;*/
    /*background-size: 100%;*/
    /*-webkit-background-size: 100% 100%, cover;*/
    /*-moz-background-size: 100% 100%, cover;*/
    /*-o-background-size: 100% 100%, cover;*/
    /*background-size: 100% 100%, cover;*/
    /*background-color: #fff;*/
  }

</style>
